import {
	BreakpointKey,
	ComponentKey,
	MediaKey,
	ResponsiveOverride,
	ResponsiveTypography,
	TemplateDesign,
	TextKey,
} from '../types';

/** Screen widths a template can change at, widest first. */
export const BREAKPOINTS: {
	key: BreakpointKey;
	width: number;
	label: string;
	device: 'laptop' | 'tablet' | 'mobile';
}[] = [
	{ key: 'w1366', width: 1366, label: 'Laptop 1366px', device: 'laptop' },
	{
		key: 'w1280',
		width: 1280,
		label: 'Small laptop 1280px',
		device: 'laptop',
	},
	{
		key: 'w1024',
		width: 1024,
		label: 'Tablet landscape 1024px',
		device: 'tablet',
	},
	{ key: 'w768', width: 768, label: 'Tablet 768px', device: 'tablet' },
	{ key: 'w640', width: 640, label: 'Mobile 640px', device: 'mobile' },
];

/** Text style values that can change per width (never the words or links). */
const RESPONSIVE_TEXT_FIELDS: ( keyof ResponsiveTypography )[] = [
	'fontFamily',
	'weight',
	'size',
	'lineHeight',
	'color',
	'colorType',
	'gradientFrom',
	'gradientTo',
	'gradientAngle',
	'align',
	'isVisible',
];

const MEDIA_FIELDS = [ 'url', 'width', 'height', 'borderRadius', 'isVisible' ];

/** Default content padding (desktop), from TemplateOne.css. */
const CONTENT_PADDING = { Top: 6, Right: 200, Bottom: 6, Left: 200 };
const CONTENT_GAP = 32;
const HEADLINE_TEXTS: TextKey[] = [
	'preText',
	'extraText',
	'highlight',
	'secondary',
];

const toNumber = ( value: string | undefined, fallback: number ): number => {
	const number = parseFloat( value ?? '' );
	return Number.isFinite( number ) ? number : fallback;
};

/* ---------------------------------------------------------------------------
 * Applying overrides
 * ------------------------------------------------------------------------- */

/**
 * A design with one width's changes on top.
 *
 * @param design   Design so far.
 * @param override Changes for one width.
 */
export const applyOverride = (
	design: TemplateDesign,
	override: ResponsiveOverride | undefined
): TemplateDesign => {
	if ( ! override ) {
		return design;
	}
	const components = { ...design.components };
	( Object.keys( override.components || {} ) as ComponentKey[] ).forEach(
		( key ) => {
			const change = override.components?.[ key ];
			if ( ! change || ! components[ key ] ) {
				return;
			}
			components[ key ] = {
				...components[ key ],
				...( change.visible !== undefined
					? { visible: change.visible }
					: {} ),
				...( change.variant ? { variant: change.variant } : {} ),
				style: {
					...components[ key ].style,
					...( change.style || {} ),
				},
			};
		}
	);

	const typography = { ...design.typography };
	( Object.keys( override.typography || {} ) as TextKey[] ).forEach(
		( key ) => {
			if ( typography[ key ] ) {
				typography[ key ] = {
					...typography[ key ],
					...override.typography?.[ key ],
				};
			}
		}
	);

	const media = { ...design.media };
	( Object.keys( override.media || {} ) as MediaKey[] ).forEach( ( key ) => {
		if ( media[ key ] ) {
			media[ key ] = { ...media[ key ], ...override.media?.[ key ] };
		}
	} );

	return { ...design, components, typography, media };
};

/**
 * Built-in responsive behaviour (what the banner always did on smaller
 * screens). It comes before the admin's own changes for the same width,
 * so every value can still be changed there.
 *
 * @param design Design so far (wider widths already applied).
 * @param key    Width.
 */
export const getBuiltInOverride = (
	design: TemplateDesign,
	key: BreakpointKey
): ResponsiveOverride => {
	const bg = design.components.background.style;
	const pad = ( side: keyof typeof CONTENT_PADDING ) =>
		toNumber( bg[ `padding${ side }` ], CONTENT_PADDING[ side ] );
	const gap = toNumber( bg.gap, CONTENT_GAP );
	const capSizes = ( keys: TextKey[], max: number ) =>
		keys.reduce(
			( all, textKey ) => {
				const size = toNumber( design.typography[ textKey ]?.size, 0 );
				return size > max
					? { ...all, [ textKey ]: { size: String( max ) } }
					: all;
			},
			{} as NonNullable< ResponsiveOverride[ 'typography' ] >
		);

	if ( key === 'w1280' ) {
		// Side images need room; padding shrinks to 32px.
		return {
			components: {
				leftImage: { visible: false },
				rightImage: { visible: false },
				background: {
					style: {
						paddingLeft: String( Math.min( pad( 'Left' ), 32 ) ),
						paddingRight: String( Math.min( pad( 'Right' ), 32 ) ),
					},
				},
			},
		};
	}

	if ( key === 'w1024' ) {
		// Items wrap onto rows; texts up to 20px.
		return {
			components: {
				background: {
					style: {
						paddingTop: String( Math.max( pad( 'Top' ), 10 ) ),
						paddingBottom: String(
							Math.max( pad( 'Bottom' ), 10 )
						),
						gap: String( Math.min( gap, 20 ) ),
					},
				},
			},
			typography: capSizes( HEADLINE_TEXTS, 20 ),
		};
	}

	if ( key === 'w768' ) {
		const cta = design.components.cta.style;
		const countdown = design.components.countdown;
		const isTextTimer = [ 'text', 'textlight', 'textnavy' ].includes(
			countdown.variant
		);
		return {
			components: {
				background: {
					style: {
						paddingLeft: String( Math.min( pad( 'Left' ), 12 ) ),
						// Room for the close button.
						paddingRight: String( Math.min( pad( 'Right' ), 28 ) ),
						gap: String( Math.min( gap, 14 ) ),
					},
				},
				cta: {
					style: {
						width: '0',
						height: String(
							Math.min( toNumber( cta.height, 36 ), 36 )
						),
						...( cta.paddingLeft ? {} : { paddingLeft: '14' } ),
						...( cta.paddingRight ? {} : { paddingRight: '14' } ),
					},
				},
			},
			typography: {
				...capSizes( HEADLINE_TEXTS, 18 ),
				...capSizes( [ 'cta' ], 15 ),
				...( isTextTimer ? capSizes( [ 'countdownDigits' ], 22 ) : {} ),
			},
		};
	}

	return {};
};

/**
 * Paths of the values an override sets, e.g. "c.cta.s.height",
 * "c.leftImage.visible", "t.highlight.size".
 *
 * @param override Changes for one width.
 */
const overridePaths = (
	override: ResponsiveOverride | undefined
): string[] => {
	if ( ! override ) {
		return [];
	}
	const paths: string[] = [];
	Object.entries( override.components || {} ).forEach(
		( [ key, change ] ) => {
			if ( change?.visible !== undefined ) {
				paths.push( `c.${ key }.visible` );
			}
			Object.keys( change?.style || {} ).forEach( ( styleKey ) =>
				paths.push( `c.${ key }.s.${ styleKey }` )
			);
		}
	);
	Object.entries( override.typography || {} ).forEach( ( [ key, change ] ) =>
		Object.keys( change || {} ).forEach( ( field ) =>
			paths.push( `t.${ key }.${ field }` )
		)
	);
	return paths;
};

/**
 * A built-in step without the values the admin set at a wider width:
 * a size set for 1024px is kept at 768px and 640px instead of being
 * capped again (the admin's choice wins over the automatic step).
 *
 * @param override Built-in changes.
 * @param userSet  Paths the admin set at wider widths.
 */
const withoutUserSet = (
	override: ResponsiveOverride,
	userSet: Set< string >
): ResponsiveOverride => {
	const components: NonNullable< ResponsiveOverride[ 'components' ] > = {};
	Object.entries( override.components || {} ).forEach(
		( [ key, change ] ) => {
			if ( ! change ) {
				return;
			}
			const next: typeof change = {};
			if (
				change.visible !== undefined &&
				! userSet.has( `c.${ key }.visible` )
			) {
				next.visible = change.visible;
			}
			const style = Object.fromEntries(
				Object.entries( change.style || {} ).filter(
					( [ styleKey ] ) =>
						! userSet.has( `c.${ key }.s.${ styleKey }` )
				)
			);
			if ( Object.keys( style ).length ) {
				next.style = style;
			}
			if ( Object.keys( next ).length ) {
				components[ key as ComponentKey ] = next;
			}
		}
	);
	const typography: NonNullable< ResponsiveOverride[ 'typography' ] > = {};
	Object.entries( override.typography || {} ).forEach(
		( [ key, change ] ) => {
			const next = Object.fromEntries(
				Object.entries( change || {} ).filter(
					( [ field ] ) => ! userSet.has( `t.${ key }.${ field }` )
				)
			);
			if ( Object.keys( next ).length ) {
				typography[ key as TextKey ] = next;
			}
		}
	);
	return { components, typography };
};

/**
 * The design as shown at a screen width: built-in behaviour and the
 * admin's changes of every width the screen is narrower than or equal to.
 * Values the admin set at a wider width are inherited as they are.
 *
 * @param design  Saved design (desktop values + changes per width).
 * @param width   Banner width in px.
 * @param exclude Leave this width's own changes out (designer: inherited).
 */
export const resolveDesignAt = (
	design: TemplateDesign,
	width: number,
	exclude?: BreakpointKey
): TemplateDesign => {
	const userSet = new Set< string >();
	return BREAKPOINTS.filter( ( bp ) => width <= bp.width ).reduce(
		( current, bp ) => {
			const withBuiltIn = applyOverride(
				current,
				withoutUserSet( getBuiltInOverride( current, bp.key ), userSet )
			);
			if ( bp.key === exclude ) {
				return withBuiltIn;
			}
			const override = design.responsive?.[ bp.key ];
			overridePaths( override ).forEach( ( path ) =>
				userSet.add( path )
			);
			return applyOverride( withBuiltIn, override );
		},
		design
	);
};

/**
 * @param key Width key.
 */
export const getBreakpoint = ( key: BreakpointKey ) =>
	BREAKPOINTS.find( ( bp ) => bp.key === key ) || BREAKPOINTS[ 0 ];

/* ---------------------------------------------------------------------------
 * Designer: editing one width
 * ------------------------------------------------------------------------- */

/**
 * The changes that turn `inherited` into `next` (only what differs).
 *
 * @param inherited Design at this width without its own changes.
 * @param next      Design as edited at this width.
 */
export const diffOverride = (
	inherited: TemplateDesign,
	next: TemplateDesign
): ResponsiveOverride | undefined => {
	const override: ResponsiveOverride = {};

	( Object.keys( next.components ) as ComponentKey[] ).forEach( ( key ) => {
		const before = inherited.components[ key ];
		const after = next.components[ key ];
		if ( ! before || ! after ) {
			return;
		}
		const change: NonNullable<
			ResponsiveOverride[ 'components' ]
		>[ ComponentKey ] = {};
		if ( before.visible !== after.visible ) {
			change.visible = after.visible;
		}
		if ( before.variant !== after.variant ) {
			change.variant = after.variant;
		}
		const style: Record< string, string > = {};
		Object.keys( after.style ).forEach( ( styleKey ) => {
			if (
				( before.style[ styleKey ] ?? '' ) !== after.style[ styleKey ]
			) {
				style[ styleKey ] = after.style[ styleKey ];
			}
		} );
		if ( Object.keys( style ).length ) {
			change.style = style;
		}
		if ( Object.keys( change ).length ) {
			override.components = { ...override.components, [ key ]: change };
		}
	} );

	( Object.keys( next.typography ) as TextKey[] ).forEach( ( key ) => {
		const before = inherited.typography[ key ];
		const after = next.typography[ key ];
		if ( ! before || ! after ) {
			return;
		}
		const change: ResponsiveTypography = {};
		RESPONSIVE_TEXT_FIELDS.forEach( ( field ) => {
			if ( before[ field ] !== after[ field ] ) {
				( change as Record< string, unknown > )[ field ] =
					after[ field ];
			}
		} );
		if ( Object.keys( change ).length ) {
			override.typography = { ...override.typography, [ key ]: change };
		}
	} );

	( Object.keys( next.media ) as MediaKey[] ).forEach( ( key ) => {
		const before = inherited.media[ key ] as unknown as Record<
			string,
			unknown
		>;
		const after = next.media[ key ] as unknown as Record< string, unknown >;
		if ( ! before || ! after ) {
			return;
		}
		const change: Record< string, unknown > = {};
		MEDIA_FIELDS.forEach( ( field ) => {
			if ( before[ field ] !== after[ field ] ) {
				change[ field ] = after[ field ];
			}
		} );
		if ( Object.keys( change ).length ) {
			override.media = { ...override.media, [ key ]: change };
		}
	} );

	return Object.keys( override ).length ? override : undefined;
};

/**
 * Store an edit made while viewing one width: style changes become that
 * width's changes; words and links always go to the desktop design (they
 * are the same on every screen).
 *
 * @param design Saved design.
 * @param key    Width being edited.
 * @param next   Design as edited at that width.
 */
export const setOverrideFromEdit = (
	design: TemplateDesign,
	key: BreakpointKey,
	next: TemplateDesign
): TemplateDesign => {
	const width = getBreakpoint( key ).width;
	const inherited = resolveDesignAt( design, width, key );
	const typography = { ...design.typography };
	( Object.keys( typography ) as TextKey[] ).forEach( ( textKey ) => {
		const edited = next.typography[ textKey ];
		if ( edited ) {
			typography[ textKey ] = {
				...typography[ textKey ],
				text: edited.text,
				...( edited.link !== undefined
					? { link: edited.link, openInNewTab: edited.openInNewTab }
					: {} ),
			};
		}
	} );
	const responsive = { ...( design.responsive || {} ) };
	const override = diffOverride( inherited, next );
	if ( override ) {
		responsive[ key ] = override;
	} else {
		delete responsive[ key ];
	}
	return {
		...design,
		name: next.name,
		typography,
		responsive: Object.keys( responsive ).length ? responsive : undefined,
	};
};

/**
 * Remove one width's changes for a component (its style, texts and images),
 * or all of that width's changes.
 *
 * @param design    Saved design.
 * @param key       Width.
 * @param component Component to clear; all when omitted.
 * @param textKeys  The component's text elements.
 * @param mediaKeys The component's images.
 */
export const clearOverride = (
	design: TemplateDesign,
	key: BreakpointKey,
	component?: ComponentKey,
	textKeys: TextKey[] = [],
	mediaKeys: MediaKey[] = []
): TemplateDesign => {
	const responsive = { ...( design.responsive || {} ) };
	const current = responsive[ key ];
	if ( ! current ) {
		return design;
	}
	const finish = (): TemplateDesign => ( {
		...design,
		responsive: Object.keys( responsive ).length ? responsive : undefined,
	} );
	if ( ! component ) {
		delete responsive[ key ];
		return finish();
	}
	const components = { ...current.components };
	delete components[ component ];
	const typography = { ...current.typography };
	textKeys.forEach( ( textKey ) => delete typography[ textKey ] );
	const media = { ...current.media };
	mediaKeys.forEach( ( mediaKey ) => delete media[ mediaKey ] );
	const next: ResponsiveOverride = {};
	if ( Object.keys( components ).length ) {
		next.components = components;
	}
	if ( Object.keys( typography ).length ) {
		next.typography = typography;
	}
	if ( Object.keys( media ).length ) {
		next.media = media;
	}
	if ( Object.keys( next ).length ) {
		responsive[ key ] = next;
	} else {
		delete responsive[ key ];
	}
	return finish();
};

/**
 * Does a width have changes for a component (or any changes)?
 *
 * @param design    Saved design.
 * @param key       Width.
 * @param component Component; any when omitted.
 * @param textKeys  The component's text elements.
 * @param mediaKeys The component's images.
 */
export const hasOverride = (
	design: TemplateDesign,
	key: BreakpointKey,
	component?: ComponentKey,
	textKeys: TextKey[] = [],
	mediaKeys: MediaKey[] = []
): boolean => {
	const current = design.responsive?.[ key ];
	if ( ! current ) {
		return false;
	}
	if ( ! component ) {
		return true;
	}
	return (
		!! current.components?.[ component ] ||
		textKeys.some( ( textKey ) => !! current.typography?.[ textKey ] ) ||
		mediaKeys.some( ( mediaKey ) => !! current.media?.[ mediaKey ] )
	);
};

/* ---------------------------------------------------------------------------
 * Loading saved data
 * ------------------------------------------------------------------------- */
type UnknownRecord = Record< string, unknown >;

const isRecord = ( value: unknown ): value is UnknownRecord =>
	!! value && typeof value === 'object' && ! Array.isArray( value );

const pickStrings = ( raw: unknown ): Record< string, string > => {
	const out: Record< string, string > = {};
	if ( isRecord( raw ) ) {
		Object.keys( raw ).forEach( ( key ) => {
			const value = raw[ key ];
			if ( typeof value === 'string' || typeof value === 'number' ) {
				out[ key ] = String( value );
			}
		} );
	}
	return out;
};

/**
 * Per-width changes as saved (keeps only known widths and value types).
 *
 * @param raw Saved `responsive` value.
 */
export const normalizeResponsive = (
	raw: unknown
): TemplateDesign[ 'responsive' ] => {
	if ( ! isRecord( raw ) ) {
		return undefined;
	}
	const result: NonNullable< TemplateDesign[ 'responsive' ] > = {};
	BREAKPOINTS.forEach( ( { key } ) => {
		const item = raw[ key ];
		if ( ! isRecord( item ) ) {
			return;
		}
		const override: ResponsiveOverride = {};
		if ( isRecord( item.components ) ) {
			const components: NonNullable<
				ResponsiveOverride[ 'components' ]
			> = {};
			Object.keys( item.components ).forEach( ( name ) => {
				const change = ( item.components as UnknownRecord )[ name ];
				if ( ! isRecord( change ) ) {
					return;
				}
				components[ name as ComponentKey ] = {
					...( typeof change.visible === 'boolean'
						? { visible: change.visible }
						: {} ),
					...( typeof change.variant === 'string' && change.variant
						? { variant: change.variant }
						: {} ),
					...( isRecord( change.style )
						? { style: pickStrings( change.style ) }
						: {} ),
				};
			} );
			override.components = components;
		}
		if ( isRecord( item.typography ) ) {
			const typography: NonNullable<
				ResponsiveOverride[ 'typography' ]
			> = {};
			Object.keys( item.typography ).forEach( ( name ) => {
				const change = ( item.typography as UnknownRecord )[ name ];
				if ( ! isRecord( change ) ) {
					return;
				}
				const values: ResponsiveTypography = {};
				RESPONSIVE_TEXT_FIELDS.forEach( ( field ) => {
					const value = change[ field ];
					if ( field === 'isVisible' ) {
						if ( typeof value === 'boolean' ) {
							values.isVisible = value;
						}
					} else if ( typeof value === 'string' ) {
						( values as UnknownRecord )[ field ] = value;
					}
				} );
				typography[ name as TextKey ] = values;
			} );
			override.typography = typography;
		}
		if ( isRecord( item.media ) ) {
			const media: NonNullable< ResponsiveOverride[ 'media' ] > = {};
			Object.keys( item.media ).forEach( ( name ) => {
				const change = ( item.media as UnknownRecord )[ name ];
				if ( ! isRecord( change ) ) {
					return;
				}
				const values: UnknownRecord = {};
				MEDIA_FIELDS.forEach( ( field ) => {
					const value = change[ field ];
					if (
						field === 'isVisible'
							? typeof value === 'boolean'
							: typeof value === 'string'
					) {
						values[ field ] = value;
					}
				} );
				media[ name as MediaKey ] = values;
			} );
			override.media = media;
		}
		result[ key ] = override;
	} );
	return Object.keys( result ).length ? result : undefined;
};
